import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import {
  AtualizarModuloRequest,
  ModuloEmpresaResponse,
  ModuloResponse,
  ModuloService,
} from '../../../services/modulo/modulo.service';
import { AdicionarEmpresasModuloModal } from '../adicionar-empresas-modulo-modal/adicionar-empresas-modulo-modal';

@Component({
  selector: 'app-editar-modulo',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    MatTableModule,
    MatSortModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    AdicionarEmpresasModuloModal,
  ],
  templateUrl: './editar-modulo.html',
  styleUrl: './editar-modulo.scss',
})
export class EditarModulo implements OnInit {
  tabAtiva: 'dados' | 'empresas' = 'dados';

  idModulo!: number;
  isLoading = false;
  isSaving = false;

  modulo: ModuloResponse = {
    idModulo: 0,
    nome: '',
    descricao: '',
    imagemUrl: '',
    ativo: true,
    idLinhaDeCuidado: 0,
    linhaDeCuidadoNome: '',
  };

  // ── Upload ────────────────────────────────────────────────
  arquivoSelecionado: File | null = null;
  arquivoTamanho = '';
  isDragging = false;

  get temImagem(): boolean {
    return !!this.arquivoSelecionado || !!this.modulo.imagemUrl;
  }

  get formularioValido(): boolean {
    return (
      !!this.modulo.nome?.trim() &&
      !!this.modulo.descricao?.trim() &&
      this.temImagem
    );
  }

  // ── Empresas ──────────────────────────────────────────────
  colunasEmpresas = ['nome', 'cnpj', 'estado', 'cidade', 'acoes'];
  @ViewChild('sortEmpresas') sortEmpresas!: MatSort;

  empresas: ModuloEmpresaResponse[] = [];
  dataSource = new MatTableDataSource<ModuloEmpresaResponse>();
  isLoadingEmpresas = false;
  termoBusca = '';
  campoBusca: string = 'nomeFantasia';
  mostrarModal = false;

  // ── Paginação ─────────────────────────────────────────────
  pageSize = 10;
  paginaAtual = 1;

  get totalEmpresas(): number {
    return this.dataSource.data.length;
  }

  get totalPaginas(): number {
    return Math.max(Math.ceil(this.totalEmpresas / this.pageSize), 1);
  }

  get empresasPaginadas(): ModuloEmpresaResponse[] {
    const inicio = (this.paginaAtual - 1) * this.pageSize;
    return this.dataSource.data.slice(inicio, inicio + this.pageSize);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private moduloService: ModuloService,
    private snackbarService: SnackbarService,
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');

    if (!idParam || isNaN(Number(idParam))) {
      this.router.navigate(['/modulos']);
      return;
    }

    this.idModulo = Number(idParam);
    this.carregarModulo();
  }

  // ── Carregar módulo ───────────────────────────────────────

  async carregarModulo(): Promise<void> {
    this.isLoading = true;
    try {
      this.modulo = await this.moduloService.buscarPorId(this.idModulo);
    } catch (err) {
      console.error('Erro ao carregar módulo:', err);
      this.router.navigate(['/modulos']);
    } finally {
      this.isLoading = false;
    }
  }

  // ── Trocar aba ────────────────────────────────────────────

  async mudarAba(aba: 'dados' | 'empresas'): Promise<void> {
    this.tabAtiva = aba;
    if (aba === 'empresas' && this.empresas.length === 0) {
      await this.carregarEmpresas();
    }
  }

  // ── Empresas ──────────────────────────────────────────────

  async carregarEmpresas(): Promise<void> {
    this.isLoadingEmpresas = true;
    try {
      this.empresas = await this.moduloService.listarEmpresas(this.idModulo);
      this.dataSource.data = [...this.empresas];
      // Conecta o sort após o *ngIf renderizar a tabela
      setTimeout(() => {
        this.dataSource.sort = this.sortEmpresas;
      });
    } catch (err) {
      console.error('Erro ao carregar empresas:', err);
    } finally {
      this.isLoadingEmpresas = false;
    }
  }

  filtrarEmpresas(): void {
    const termo = this.termoBusca.trim().toLowerCase();
    this.dataSource.data = this.empresas.filter((e) => {
      if (!termo) return true;
      const valor = String((e as any)[this.campoBusca] || '').toLowerCase();
      return valor.includes(termo);
    });
    this.paginaAtual = 1;
  }

  async removerEmpresa(empresa: ModuloEmpresaResponse): Promise<void> {
    try {
      await this.moduloService.removerEmpresa(this.idModulo, empresa.idEmpresa);
      this.snackbarService.success('Empresa removida com sucesso!');
      await this.carregarEmpresas();
    } catch (err) {
      console.error('Erro ao remover empresa:', err);
    }
  }

  abrirModal(): void {
    this.mostrarModal = true;
  }

  fecharModal(): void {
    this.mostrarModal = false;
  }

  async onEmpresasAdicionadas(): Promise<void> {
    this.mostrarModal = false;
    await this.carregarEmpresas();
  }

  paginaAnterior(): void {
    if (this.paginaAtual > 1) this.paginaAtual--;
  }

  proximaPagina(): void {
    if (this.paginaAtual < this.totalPaginas) this.paginaAtual++;
  }

  // ── Upload ────────────────────────────────────────────────

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) this.processarArquivo(input.files[0]);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer?.files[0];
    if (file) this.processarArquivo(file);
  }

  private processarArquivo(file: File): void {
    const permitidos = ['image/jpeg', 'image/png'];
    const maxBytes = 5 * 1024 * 1024;

    if (!permitidos.includes(file.type)) {
      this.snackbarService.error('Apenas arquivos .jpeg e .png são permitidos.');
      return;
    }
    if (file.size > maxBytes) {
      this.snackbarService.error('O arquivo deve ter no máximo 5 MB.');
      return;
    }

    this.arquivoSelecionado = file;
    this.arquivoTamanho = (file.size / (1024 * 1024)).toFixed(1) + ' MB';
  }

  removerArquivo(): void {
    this.arquivoSelecionado = null;
    this.arquivoTamanho = '';
  }

  // ── Salvar ────────────────────────────────────────────────

  cancelar(): void {
    this.router.navigate(['/modulos']);
  }

  async salvar(): Promise<void> {
    if (!this.formularioValido) return;

    this.isSaving = true;
    try {
      if (this.arquivoSelecionado) {
        const atualizado = await this.moduloService.uploadImagem(
          this.idModulo,
          this.arquivoSelecionado,
        );
        this.modulo.imagemUrl = atualizado.imagemUrl;
      }

      const dto: AtualizarModuloRequest = {
        nome: this.modulo.nome,
        descricao: this.modulo.descricao,
      };

      await this.moduloService.atualizar(this.idModulo, dto);
      this.snackbarService.success('Módulo atualizado com sucesso!');
      this.router.navigate(['/modulos']);
    } catch (err) {
      console.error('Erro ao salvar módulo:', err);
    } finally {
      this.isSaving = false;
    }
  }
}